import React, { createRef, forwardRef, memo, useEffect, useState } from 'react';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { useContextMenu } from 'components/ContextMenu/ContextMenu';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import classNames from 'classnames';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';

import useStyles from './Tree.styles';

const treeItemRefs = {};

const Tree = forwardRef(({ className, items, expanded, selected }, ref) => {
  const { showContextMenu } = useContextMenu();
  const classes = useStyles();

  // the IDs of the expanded tree items
  const [expandedIds, setExpandedIds] = useState([]);
  // the ID of the selected tree item
  const [selectedId, setSelectedId] = useState(null);

  // sets the tree items to expand (if any)
  useEffect(() => {
    if (expanded) {
      // add to the existing expanded IDs instead of replacing them
      setExpandedIds([...expandedIds, ...expanded]);
    }
  }, [expanded]);

  // sets the tree item to select (if any)
  useEffect(() => {
    if (selected) {
      setSelectedId(selected);
      setTimeout(() => {
        const treeItem = treeItemRefs[selected]?.current;
        if (treeItem) {
          treeItem.scrollIntoView({
            behavior: 'auto',
            block: 'center',
            inline: 'center'
          });
        }
      }, 1000);
    }
  }, [selected]);

  // handles clicks on the tree item and its expand / collapse icon
  const onTreeItemClick = (event, item) => {
    // reset the selected item
    setSelectedId(null);
    // specifies whether the tree item is being expanded or not
    const isExpanded = expandedIds.includes(item.id);
    // check if the click was on the expand / collapse icon (code copied from
    // https://github.com/mui/material-ui/issues/29491#issuecomment-1166575101 since 'onIconClick' got removed in MUI version 5)
    const isExpandCollapseIconClick = !!event.target.closest('.MuiTreeItem-iconContainer');
    // call the item's 'onClick' handler
    item.onClick?.(event, !isExpanded, isExpandCollapseIconClick);
    // if the event default hasn't been prevented...
    if (!event.defaultPrevented) {
      // ...expand or collapse the tree item by adding or removing its ID from expanded array
      const newExpanded = isExpanded ? expandedIds.filter(id => id !== item.id) : [...expandedIds, item.id];
      setExpandedIds(newExpanded);
    }
  };

  const renderTreeItem = item => {
    const itemRef = createRef(null);
    // keep track of the item ref
    treeItemRefs[item.id] = itemRef;
    // the number of children if any are present, falling back to the child count property
    const childCount = item.children ? item.children.length : item.childCount;
    // the progress to show instead of the expand/collapse icon when a tree item's children are being loaded
    const progress = item.isLoading && <CircularProgress size={10} color="inherit" />;
    // the icon component for the tree item
    const { icon: Icon } = item;
    // specifies whether or not the item has actions
    const hasActions = item.actions?.length > 0;
    // the shortcut actions to be rendered as icon buttons inside the tree item
    const shortcutActions = item.actions?.filter(action => action.showShortcut);

    return (
      <TreeItem
        ref={itemRef}
        label={
          <div className={classes.labelRoot}>
            {!!Icon && <Icon className={classes.labelIcon} />}
            <Typography variant="body2" className={classes.primaryLabel}>
              {item.label}
            </Typography>
            {childCount > 0 && (
              <Typography
                variant="caption"
                className={classNames(classes.secondaryLabel, hasActions ? 'hideOnHover' : null)}
              >
                {childCount}
              </Typography>
            )}
            {hasActions && (
              <div className={classes.actions}>
                {shortcutActions.map(({ id, icon: Icon, label, onClick }) => (
                  <Tooltip key={label} title={label} placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={event => {
                        // for nested tree items, prevent the event from propagating to enclosing tree items
                        event.stopPropagation();
                        // execute the action's click handler
                        onClick?.();
                      }}
                      data-id={id}
                    >
                      <Icon />
                    </IconButton>
                  </Tooltip>
                ))}
                {item.actions?.length > shortcutActions.length && (
                  <Tooltip title="More" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={event => {
                        // for nested tree items, prevent the event from propagating to enclosing tree items
                        event.stopPropagation();
                        // select the tree item for as long as the context menu is shown
                        setSelectedId(item.id);
                        // show the context menu with all actions
                        showContextMenu(event, item.actions, () => setSelectedId(null), true);
                      }}
                      data-test="treeItemMore"
                    >
                      <MoreHorizRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        }
        className={selectedId === item.id ? 'selected' : null}
        classes={{
          root: classes.item,
          content: classes.content,
          expanded: classes.expanded,
          selected: classes.selected,
          group: classes.group,
          label: classes.label
        }}
        key={item.id}
        nodeId={item.id}
        data-test="treeItem"
        data-id={item.id}
        expandIcon={progress}
        collapseIcon={progress}
        onClick={event => onTreeItemClick(event, item)}
        onContextMenu={event => {
          // for nested tree items, prevent the event from propagating to enclosing tree items
          event.stopPropagation();
          if (item.actions?.length) {
            // select the tree item for as long as the context menu is shown
            setSelectedId(item.id);
            // show the context menu with all actions
            showContextMenu(event, item.actions, () => setSelectedId(null));
          }
        }}
      >
        {item.children?.map(item => renderTreeItem(item)) ||
          // if there are no children but the child count indicates there are, or if the children are loading,
          // then render a stub item s.t. the expand icon or progress gets rendered
          ((item.childCount > 0 || item.isLoading) && [<div key="stub" />])}
      </TreeItem>
    );
  };

  return (
    <TreeView
      ref={ref}
      className={classNames(classes.root, className)}
      defaultCollapseIcon={<ExpandMoreRoundedIcon />}
      defaultExpandIcon={<ChevronRightRoundedIcon />}
      expanded={expandedIds}
    >
      {items.map(item => renderTreeItem(item))}
    </TreeView>
  );
});

export default memo(Tree);
